import { Button, Form, Input, InputRef } from "antd";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { CurrentScaningRef } from "./helper";
import { delay } from "@/utils/delay";

interface CurrentScanningProps {
  className?: string;
  palletId: string;
  orderId: string;
  productScanSerialNoMap: Record<string, boolean>;
  scanMode: "auto" | "manual";
  autoPrintCartonLabel: boolean;
  onError: (error: string) => void;
  submitPalletProduct: (
    orderId: string,
    palletId: string,
    sku: string,
    serialNo?: string,
    printCartonLabel?: boolean,
  ) => Promise<void>;
}

const CurrentScanning = forwardRef<CurrentScaningRef, CurrentScanningProps>(
  function CurrentScanning(props, ref) {
    const {
      className,
      palletId,
      orderId,
      productScanSerialNoMap,
      scanMode,
      autoPrintCartonLabel,
      onError,
      submitPalletProduct,
    } = props;

    const [form] = Form.useForm();
    const skuInputRef = useRef<InputRef>(null);
    const serialNoInputRef = useRef<InputRef>(null);
    const [loading, setLoading] = useState(false);
    const palletIdRef = useRef<string | undefined>(undefined);
    const orderIdRef = useRef<string | undefined>(undefined);

    const skuInput = Form.useWatch("sku", form);
    const scanSerialNo = useMemo(() => {
      if (skuInput == null) return false;
      const tempSKU = skuInput?.trim();
      if (tempSKU == null || tempSKU === "") return false;
      return productScanSerialNoMap?.[tempSKU] ?? false;
    }, [skuInput, productScanSerialNoMap]);

    const clearInputs = useCallback(() => {
      form.setFieldsValue({ sku: "", serialNo: "" });
      if (
        serialNoInputRef.current != null &&
        serialNoInputRef.current.input != null
      ) {
        serialNoInputRef.current.input.value = "";
      }
      if (skuInputRef.current != null) {
        if (skuInputRef.current.input != null) {
          skuInputRef.current.input.value = "";
        }
        skuInputRef.current.focus();
      }
    }, [form]);

    const clickSubmit = useCallback(() => {
      if (loading) {
        return;
      }
      if (serialNoInputRef.current != null) {
        serialNoInputRef.current.blur();
      }
      if (skuInputRef.current != null) {
        skuInputRef.current.blur();
      }
      form
        .validateFields()
        .then((values) => {
          return submitPalletProduct(
            values.orderId as string,
            values.palletId as string,
            values.sku as string,
            values.serialNo as string,
            autoPrintCartonLabel,
          ).then(() => {
            clearInputs();
          });
        })
        .catch((error) => {
          onError(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }, [
      form,
      loading,
      submitPalletProduct,
      onError,
      clearInputs,
      autoPrintCartonLabel,
    ]);

    const handleInputEnterPressed = useCallback(() => {
      if (loading) {
        return;
      }
      const allValues = form.getFieldsValue();
      const validSKU =
        allValues.sku?.trim() != null && allValues.sku?.trim() != "";
      const validSerialNo =
        allValues.serialNo?.trim() != null && allValues.serialNo?.trim() != "";

      let serialNoSatisfied = true;
      if (validSKU) {
        const skuInput = allValues.sku?.trim();
        if (productScanSerialNoMap?.[skuInput] ?? false) {
          serialNoSatisfied = validSerialNo;
        }
      }

      if (validSKU && serialNoSatisfied) {
        if (scanMode === "manual") return;
        clickSubmit();
      } else if (validSKU) {
        if (serialNoInputRef.current != null) {
          serialNoInputRef.current.focus();
        }
      } else if (validSerialNo) {
        if (skuInputRef.current != null) {
          skuInputRef.current.focus();
        }
      }
    }, [form, loading, scanMode, clickSubmit, productScanSerialNoMap]);

    const reset = useCallback(
      (palletId: string, orderId: string) => {
        if (skuInputRef.current != null) {
          delay(() => {
            if (skuInputRef.current != null) {
              skuInputRef.current.focus();
            }
          }, 100);
        }
        form.setFieldsValue({
          palletId: palletId,
          orderId: orderId,
          sku: "",
          serialNo: "",
        });
        palletIdRef.current = palletId;
        orderIdRef.current = orderId;
      },
      [form],
    );

    useImperativeHandle(
      ref,
      () => ({
        reset: reset,
      }),
      [reset],
    );

    useEffect(() => {
      form.setFieldsValue({ sku: "", serialNo: "" });
      skuInputRef.current?.focus();
    }, [scanMode, form]);

    return (
      <div className={classNames(className, "flex flex-col px-6 py-4")}>
        <Form layout="vertical" form={form} disabled={loading}>
          <Form.Item
            required
            name="palletId"
            key="palletId"
            hidden
            initialValue={palletId}
          />
          <Form.Item
            required
            name="orderId"
            hidden
            key="orderId"
            initialValue={orderId}
          />
          <Form.Item
            required
            name="sku"
            label={<span className="font-bold text-[24px]">SKU Scanned:</span>}
            rules={[{ required: true, message: "SKU is required" }]}
          >
            <Input
              size="large"
              className="!text-[24px]"
              ref={skuInputRef}
              allowClear
              disabled={loading}
              placeholder="Scan or Enter SKU"
              onPressEnter={handleInputEnterPressed}
            />
          </Form.Item>
          {scanSerialNo && (
            <Form.Item
              name="serialNo"
              required
              label={
                <span className="font-bold text-[24px]">
                  Serial No. Scanned:
                </span>
              }
              rules={[{ required: true, message: "Serial No. is required" }]}
            >
              <Input
                size="large"
                className="!text-[24px]"
                allowClear
                disabled={loading}
                ref={serialNoInputRef}
                placeholder="Scan or Enter Serial No."
                onPressEnter={handleInputEnterPressed}
              />
            </Form.Item>
          )}
          <Form.Item>
            <Button
              color="blue"
              size="large"
              className="w-full"
              variant="outlined"
              disabled={scanMode === "auto" || loading}
              onClick={clickSubmit}
            >
              {scanMode === "auto"
                ? "Auto Submit after scanned"
                : "Manual Click to Submit"}
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  },
);

CurrentScanning.displayName = "CurrentScanning";

export default CurrentScanning;
